import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";

import { DEFAULT_STATE } from "./constants";
import { getMeta, getDashboard } from "./api";
import i18n from "../../i18n";

const Ctx = createContext(null);
export const useVSU = () => useContext(Ctx);

const normLang = (l) =>
  String(l ?? "")
    .toLowerCase()
    .startsWith("k")
    ? "kk"
    : "ru";

const toLng = (l) => (l === "kk" ? "kz" : "ru");

export function VideoStreamProvider({
  children,
  lang: langProp,
  onLangChange,
}) {
  const langControlled = langProp != null;
  const [langState, setLangState] = useState(DEFAULT_STATE.lang);
  const lang = langControlled ? normLang(langProp) : langState;
  const setLang = useCallback(
    (next) =>
      langControlled ? onLangChange && onLangChange(next) : setLangState(next),
    [langControlled, onLangChange]
  );

  const [dateKind, setDateKind] = useState(DEFAULT_STATE.dateKind);
  const [dateFrom, setDateFrom] = useState(DEFAULT_STATE.dateFrom);
  const [dateTo, setDateTo] = useState(DEFAULT_STATE.dateTo);
  const [filterSource, setFilterSource] = useState(DEFAULT_STATE.filterSource);
  const [filterAddress, setFilterAddress] = useState(
    DEFAULT_STATE.filterAddress
  );
  const [filterLocation, setFilterLocation] = useState(
    DEFAULT_STATE.filterLocation
  );

  const [search, setSearch] = useState(DEFAULT_STATE.search);
  const [sort, setSort] = useState({
    col: DEFAULT_STATE.sortCol,
    dir: DEFAULT_STATE.sortDir,
  });

  const [selectedId, setSelectedId] = useState(DEFAULT_STATE.selectedId);
  const [playing, setPlaying] = useState(DEFAULT_STATE.playing);

  const [meta, setMeta] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const t = useCallback((k) => i18n.t(`videoStreamPage.${k}`), []);

  useEffect(() => {
    let alive = true;
    getMeta()
      .then((m) => alive && setMeta(m))
      .catch(
        () =>
          alive &&
          setMeta({
            sources: [],
            addresses: [],
            locations: [],
            date_bounds: {},
          })
      );
    return () => {
      alive = false;
    };
  }, []);

  const reqId = useRef(0);
  const loadDashboard = useCallback(() => {
    const my = ++reqId.current;
    setLoading(true);
    setError(false);
    getDashboard({
      date_kind: dateKind,
      date_from: dateFrom,
      date_to: dateTo,
      source_name: filterSource,
      address: filterAddress,
      location: filterLocation,
    })
      .then((data) => {
        if (my !== reqId.current) return;
        setDashboard(data);

        setSelectedId((prev) => {
          const items = data.items;
          if (!items.length) return null;
          return items.some((s) => s.id === prev) ? prev : items[0].id;
        });
        setPlaying(false);
        setLoading(false);
      })
      .catch(() => {
        if (my !== reqId.current) return;
        setError(true);
        setLoading(false);
      });
  }, [dateKind, dateFrom, dateTo, filterSource, filterAddress, filterLocation]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const selectStream = useCallback((id) => {
    setSelectedId((prev) => (prev === id ? prev : (setPlaying(false), id)));
  }, []);

  const resetFilters = useCallback(() => {
    setDateKind(DEFAULT_STATE.dateKind);
    setDateFrom("");
    setDateTo("");
    setFilterSource("");
    setFilterAddress("");
    setFilterLocation("");
    setSearch("");
    setSort({ col: DEFAULT_STATE.sortCol, dir: DEFAULT_STATE.sortDir });
  }, []);

  const toggleSort = useCallback((col) => {
    setSort((s) => (s.col === col ? { col, dir: -s.dir } : { col, dir: 1 }));
  }, []);

  const items = dashboard?.items ?? [];
  const summary = dashboard?.summary ?? null;
  const breakdowns = dashboard?.breakdowns ?? null;

  const selected = useMemo(
    () =>
      selectedId == null
        ? null
        : items.find((s) => s.id === selectedId) ?? null,
    [items, selectedId]
  );

  const filterCaption = useMemo(() => {
    const kindLbl = (dateKind === "plan"
      ? t("tPlan")
      : t("tFact")
    ).toLowerCase();
    const fmt = (d) => (d ? d.split("-").reverse().join(".") : "…");
    const parts = [];
    if (dateFrom || dateTo)
      parts.push(
        `${t("period")}: ${kindLbl} ${fmt(dateFrom)} — ${fmt(dateTo)}`
      );
    if (filterSource) parts.push(`${t("fSrc")}: ${filterSource}`);
    if (filterAddress) parts.push(`${t("fAddr")}: ${filterAddress}`);
    if (filterLocation) parts.push(`${t("fLoc")}: ${filterLocation}`);
    return parts.length
      ? `· ${t("onFilter")}: ${parts.join(" · ")}`
      : `· ${t("noFilter")}`;
  }, [
    dateKind,
    dateFrom,
    dateTo,
    filterSource,
    filterAddress,
    filterLocation,
    t,
  ]);

  const toggleBreakdownFilter = useCallback((field, valueRu) => {
    const setter =
      field === "by_source"
        ? setFilterSource
        : field === "by_address"
        ? setFilterAddress
        : setFilterLocation;
    setter((cur) => (cur === valueRu ? "" : valueRu));
  }, []);

  const value = {
    lang,
    setLang,
    langControlled,
    t,
    dateKind,
    setDateKind,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    filterSource,
    setFilterSource,
    filterAddress,
    setFilterAddress,
    filterLocation,
    setFilterLocation,
    resetFilters,
    toggleBreakdownFilter,
    search,
    setSearch,
    sort,
    toggleSort,
    selectedId,
    selectStream,
    selected,
    playing,
    setPlaying,
    meta,
    dashboard,
    items,
    summary,
    breakdowns,
    loading,
    error,
    reload: loadDashboard,
    filterCaption,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
